import logger from '../utils/logger';

/**
 * GMB Data Mapper Service
 * Maps Google My Business profile data to template formats
 */

class GMBDataMapper {
  /**
   * Main mapping function that routes to appropriate template mapper
   */
  mapGMBDataToTemplate(gmbData, templateId) {
    switch (templateId) {
      case 1:
        return this.mapToTemplate1(gmbData);
      case 2:
        return this.mapToTemplate2(gmbData);
      case 3:
        return this.mapToTemplate3(gmbData);
      case 4:
        return this.mapToTemplate4(gmbData);
      case 5:
        return this.mapToTemplate5(gmbData);
      case 6:
        return this.mapToTemplate6(gmbData);
      case 7:
        return this.mapToTemplate7(gmbData);
      default:
        return this.mapToTemplate1(gmbData);
    }
  }

  /**
   * Map GMB location data to Template 1 (Medical Center) format
   */
  mapToTemplate1(gmbData) {
    const location = gmbData.location || {};
    const businessInfo = location.profile || {};
    const address = location.storefrontAddress || {};
    const phoneNumbers = location.phoneNumbers || {};
    const photos = this.generatePhotos(gmbData.photos, 'medical');

    // Only use real GMB data, no mock fallbacks
    const businessName = location.title || '';
    const businessDescription = businessInfo.description || '';
    const ratingInfo = this.extractRatingInfo(gmbData);

    // Extract GMB categories for tag generation
    const gmbCategories = this.extractGMBCategories(location.categories);

    return {
      businessInfo: {
        name: businessName,
        tagline: businessDescription ? `${businessName} - Your trusted partner` : '',
        description: businessDescription,
        logo: null,
        heroTitle: businessName,
        heroSubtitle: businessDescription,
        ctaText: businessName ? "CONTACT US" : '',
        heroImage: photos[0]?.url || null,
        rating: ratingInfo.rating,
        reviewCount: ratingInfo.reviewCount,
        categories: gmbCategories // Add GMB categories to businessInfo
      },
      categories: gmbCategories, // Also add at top level for TagsSection
      services: this.generateServices(location.categories, 'medical', gmbData.services, gmbData),
      aboutUs: businessDescription ? {
        title: `About ${businessName}`,
        description: businessDescription,
        additionalText: `Learn more about ${businessName} and our commitment to excellence.`,
        ctaText: "Learn More",
        image: photos[1]?.url || null
      } : null,
      photos: photos,
      latestUpdates: this.generateUpdates(gmbData),
      testimonials: this.generateTestimonials(gmbData),
      faq: this.generateFAQ('medical', gmbData),
      contactInfo: {
        address: this.formatAddress(address),
        email: businessName ? `info@${businessName.toLowerCase().replace(/\s+/g, '')}.com` : '',
        phone: phoneNumbers.primaryPhone || '',
        workingHours: this.formatHours(location.regularHours),
        sundayHours: location.regularHours ? "SUNDAY : CLOSED" : ''
      },
      socialMedia: {
        instagram: "#",
        facebook: "#",
        twitter: "#"
      },
      footerLinks: {
        services: [],
        company: businessName ? ["About Us", "Services", "Contact Us"] : []
      },
      newsletter: businessName ? {
        title: `Stay Updated with ${businessName}`,
        placeholder: "Enter Your Email",
        buttonText: "Subscribe"
      } : null,
      location: {
        title: "Find Us",
        subtitle: "Visit our location or reach out to us for more information",
        address: this.formatAddress(address),
        lat: location.latlng?.latitude || null,
        lng: location.latlng?.longitude || null
      },
      gmbFeed: this.generateGMBFeed(gmbData)
    };
  }

  /**
   * Map GMB location data to Template 2 (Automotive) format
   */
  mapToTemplate2(gmbData) {
    const location = gmbData.location || {};
    const businessInfo = location.profile || {};
    const address = location.storefrontAddress || {};
    const phoneNumbers = location.phoneNumbers || {};
    const photos = this.generatePhotos(gmbData.photos, 'automotive');

    // Only use real GMB data, no mock fallbacks
    const businessName = location.title || '';
    const businessDescription = businessInfo.description || '';
    const ratingInfo = this.extractRatingInfo(gmbData);

    return {
      topContactInfo: {
        hours: this.formatHours(location.regularHours),
        phone: phoneNumbers.primaryPhone || '',
        address: this.formatAddress(address),
        socialMedia: {
          facebook: "#",
          twitter: "#",
          instagram: "#",
          linkedin: "#"
        }
      },
      businessInfo: {
        name: businessName,
        logo: null,
        heroTitle: businessName,
        heroSubtitle: businessDescription,
        ctaText: businessName ? "Contact Us" : '',
        heroImages: photos.length > 0 ? [photos[0].url] : [],
        rating: ratingInfo.rating,
        reviewCount: ratingInfo.reviewCount
      },
      navigation: {
        menuItems: businessName ? ["Home", "Services", "About Us", "Contact"] : []
      },
      aboutUs: businessDescription ? {
        title: "About Us",
        heading: businessName,
        description: businessDescription,
        features: [],
        ctaText: "Learn More",
        image: photos[1]?.url || null
      } : null,
      services: this.generateServices(location.categories, 'automotive', gmbData.services, gmbData),
      photos: photos,
      testimonials: this.generateTestimonials(gmbData),
      technologies: this.generateTechnologies(gmbData),
      faq: this.generateFAQ('automotive', gmbData),
      contactInfo: {
        title: businessName ? `Contact ${businessName}` : '',
        description: businessDescription ? `Get in touch with ${businessName} for more information.` : '',
        ctaText: "Contact",
        details: {
          address: {
            country: address.countryCode || '',
            street: this.formatAddress(address),
            city: address.locality || ''
          },
          phone: phoneNumbers.primaryPhone || '',
          email: businessName ? `info@${businessName.toLowerCase().replace(/\s+/g, '')}.com` : '',
          hours: {
            weekdays: this.formatHours(location.regularHours) || '',
            saturday: '',
            sunday: ''
          }
        },
        map: null
      },
      footer: {
        logo: null,
        tagline: businessName ? `${businessName} - Professional Services` : '',
        newsletter: businessName ? {
          title: `Stay Updated with ${businessName}`,
          subtitle: "Your Email",
          placeholder: "Enter Your Email",
          buttonText: "Subscribe"
        } : null,
        sections: {
          socials: {
            title: "Follow Us",
            links: ["Facebook", "Twitter", "Instagram"]
          },
          menu: {
            title: "Menu",
            links: businessName ? ["Home", "Services", "About Us", "Contact"] : []
          },
          contact: {
            title: "Contact",
            email: businessName ? `info@${businessName.toLowerCase().replace(/\s+/g, '')}.com` : ''
          }
        },
        copyright: {
          brand: businessName || "Business",
          text: "© 2025. All Rights Reserved."
        }
      },
      location: {
        title: "Find Us",
        subtitle: "Visit our location or reach out to us for more information",
        address: this.formatAddress(address),
        lat: location.latlng?.latitude || null,
        lng: location.latlng?.longitude || null
      },
      gmbFeed: this.generateGMBFeed(gmbData)
    };
  }

  /**
   * Helper method to format address
   */
  formatAddress(address) {
    if (!address) return '';

    const parts = [];
    if (address.addressLines) parts.push(...address.addressLines);
    if (address.locality) parts.push(address.locality);
    if (address.administrativeArea) parts.push(address.administrativeArea);
    if (address.postalCode) parts.push(address.postalCode);

    return parts.length > 0 ? parts.join(', ') : '';
  }

  /**
   * Helper method to format business hours
   */
  formatHours(regularHours) {
    if (!regularHours || !regularHours.periods) return '';

    try {
      const periods = regularHours.periods;
      if (periods.length === 0) return '';

      const firstPeriod = periods[0];
      const openDay = this.getDayName(firstPeriod.openDay);
      const closeDay = this.getDayName(firstPeriod.closeDay);
      const openTime = this.formatTime(firstPeriod.openTime);
      const closeTime = this.formatTime(firstPeriod.closeTime);

      return `${openDay}-${closeDay}: ${openTime} – ${closeTime}`;
    } catch (error) {
      return '';
    }
  }

  /**
   * Helper method to get day name
   */
  getDayName(day) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[day] || 'Mon';
  }

  /**
   * Helper method to format time
   */
  formatTime(time) {
    if (!time) return '';

    try {
      const hours = parseInt(time.hours || 0);
      const minutes = parseInt(time.minutes || 0);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : '';

      return `${displayHours}${displayMinutes} ${ampm}`;
    } catch (error) {
      return '';
    }
  }

  /**
   * Generate services based on GMB services data or business categories
   */
  generateServices(categories, templateType, gmbServices = null, gmbData = null) {
    // Detect business category for dynamic service generation
    const detectedCategory = gmbData ? this.detectBusinessCategory(gmbData) : templateType;

    // Get photos from GMB data
    const gmbPhotos = gmbData && gmbData.photos ? this.generatePhotos(gmbData.photos, templateType) : [];
    logger.log('[GMBDataMapper] generateServices - GMB photos available:', gmbPhotos.length);

    // Use GMB services data if available
    if (gmbServices && gmbServices.length > 0) {
      return gmbServices.slice(0, 8).map((service, index) => ({
        id: index + 1,
        name: service.displayName || service.name,
        description: service.description || this.getServiceDescription(service.displayName || service.name, detectedCategory),
        title: service.displayName || service.name,
        category: service.category || 'Service',
        image: gmbPhotos[index % gmbPhotos.length]?.url || `/images/${detectedCategory}-service-${index + 1}.jpg`
      }));
    }

    // Use categories if available with dynamic descriptions
    if (categories && categories.length > 0) {
      return categories.slice(0, 8).map((category, index) => ({
        id: index + 1,
        name: category.displayName || category.name,
        description: this.getServiceDescription(category.displayName || category.name, detectedCategory),
        title: category.displayName || category.name,
        category: category.displayName || category.name,
        image: gmbPhotos[index % gmbPhotos.length]?.url || `/images/${detectedCategory}-service-${index + 1}.jpg`
      }));
    }

    // Generate category-specific default services if no GMB data
    return this.getDefaultServices(detectedCategory, gmbPhotos);
  }

  /**
   * Get service description based on business category
   */
  getServiceDescription(serviceName, businessCategory) {
    const descriptions = {
      medical: `Professional ${serviceName} healthcare services with experienced medical staff`,
      real_estate: `Expert ${serviceName} real estate services to help you buy, sell, or invest`,
      automotive: `Professional ${serviceName} automotive services with certified technicians`,
      restaurant: `Quality ${serviceName} dining experience with fresh ingredients`,
      legal: `Professional ${serviceName} legal services with experienced attorneys`,
      beauty: `Premium ${serviceName} beauty services with skilled professionals`,
      retail: `Quality ${serviceName} products with excellent customer service`,
      general: `Professional ${serviceName} services tailored to your needs`
    };

    return descriptions[businessCategory] || descriptions.general;
  }

  /**
   * Get default services for business category with real GMB photos
   */
  getDefaultServices(businessCategory, gmbPhotos = []) {
    const defaultServices = {
      medical: [
        { name: 'Medical Consultation', description: 'Comprehensive medical consultations with experienced doctors' },
        { name: 'Health Checkups', description: 'Regular health screenings and preventive care services' },
        { name: 'Diagnostic Services', description: 'Advanced diagnostic testing and medical imaging' },
        { name: 'Specialist Care', description: 'Specialized medical treatments and expert care' },
        { name: 'Emergency Services', description: '24/7 emergency medical care and support' },
        { name: 'Preventive Care', description: 'Preventive healthcare and wellness programs' }
      ],
      real_estate: [
        { name: 'Property Sales', description: 'Expert assistance in buying and selling residential properties' },
        { name: 'Property Management', description: 'Professional property management and rental services' },
        { name: 'Market Analysis', description: 'Comprehensive market analysis and property valuations' },
        { name: 'Commercial Properties', description: 'Commercial real estate sales and leasing services' },
        { name: 'Investment Properties', description: 'Real estate investment opportunities and consulting' },
        { name: 'Property Consultation', description: 'Expert real estate advice and property guidance' }
      ],
      automotive: [
        { name: 'Auto Repair', description: 'Professional automotive repair services for all vehicle makes' },
        { name: 'Maintenance Services', description: 'Regular maintenance to keep your vehicle running smoothly' },
        { name: 'Diagnostic Services', description: 'Advanced automotive diagnostic and troubleshooting' },
        { name: 'Oil Changes', description: 'Quick and professional oil change services' },
        { name: 'Brake Services', description: 'Brake inspection, repair, and replacement services' },
        { name: 'Tire Services', description: 'Tire sales, installation, and rotation services' }
      ],
      restaurant: [
        { name: 'Dine-In Service', description: 'Comfortable dining experience with quality cuisine' },
        { name: 'Takeout Orders', description: 'Quick and convenient takeout service' },
        { name: 'Catering Services', description: 'Professional catering for events and special occasions' },
        { name: 'Private Events', description: 'Private dining spaces for special celebrations' },
        { name: 'Delivery Service', description: 'Fast and reliable food delivery to your door' },
        { name: 'Bar & Drinks', description: 'Full bar service with specialty cocktails and beverages' }
      ],
      legal: [
        { name: 'Legal Consultation', description: 'Professional legal advice and consultation services' },
        { name: 'Document Preparation', description: 'Expert preparation of legal documents and contracts' },
        { name: 'Court Representation', description: 'Experienced legal representation in court proceedings' },
        { name: 'Contract Review', description: 'Thorough review and analysis of legal contracts' },
        { name: 'Estate Planning', description: 'Comprehensive estate planning and will preparation' },
        { name: 'Business Law', description: 'Business legal services and corporate counsel' }
      ],
      beauty: [
        { name: 'Hair Services', description: 'Professional hair cutting, styling, and coloring services' },
        { name: 'Skin Care', description: 'Advanced skincare treatments and facial services' },
        { name: 'Wellness Services', description: 'Relaxing wellness and spa treatments' },
        { name: 'Nail Services', description: 'Manicure, pedicure, and nail art services' },
        { name: 'Makeup Services', description: 'Professional makeup application and consultation' },
        { name: 'Massage Therapy', description: 'Therapeutic massage and relaxation treatments' }
      ],
      retail: [
        { name: 'Product Sales', description: 'Quality products with excellent customer service' },
        { name: 'Customer Support', description: 'Dedicated customer support and assistance' },
        { name: 'Product Consultation', description: 'Expert advice to help you choose the right products' },
        { name: 'Online Shopping', description: 'Convenient online shopping with home delivery' },
        { name: 'Returns & Exchanges', description: 'Easy returns and exchange process' },
        { name: 'Special Orders', description: 'Custom orders and special product requests' }
      ],
      general: [
        { name: 'Professional Services', description: 'Expert professional services tailored to your needs' },
        { name: 'Consultation', description: 'Free consultation to discuss your requirements' },
        { name: 'Custom Solutions', description: 'Customized solutions for your specific needs' },
        { name: 'Quality Assurance', description: 'Guaranteed quality and customer satisfaction' },
        { name: 'Support Services', description: 'Ongoing support and maintenance services' },
        { name: 'Expert Advice', description: 'Professional guidance and recommendations' }
      ]
    };

    const services = defaultServices[businessCategory] || defaultServices.general;
    logger.log('[GMBDataMapper] getDefaultServices - Using', services.length, 'services for category:', businessCategory);
    logger.log('[GMBDataMapper] getDefaultServices - GMB photos available:', gmbPhotos.length);

    return services.map((service, index) => ({
      id: index + 1,
      name: service.name,
      title: service.name,
      description: service.description,
      category: businessCategory,
      image: gmbPhotos[index % gmbPhotos.length]?.url || `/images/${businessCategory}-service-${index + 1}.jpg`
    }));
  }

  /**
   * Generate dynamic testimonials based on business category
   * USES REAL GMB REVIEWS when available - ONLY shows reviews with written text
   */
  generateTestimonials(gmbData = null) {
    // If we have real GMB reviews, use them instead of mock data
    if (gmbData && gmbData.reviews && gmbData.reviews.reviews && gmbData.reviews.reviews.length > 0) {
      logger.log('[GMBDataMapper] Processing GMB reviews for testimonials. Total reviews:', gmbData.reviews.reviews.length);

      // Filter to only include reviews with actual written text
      const reviewsWithText = gmbData.reviews.reviews.filter(review => {
        const reviewText = review.comment || review.text || review.reviewReply?.comment || '';
        const hasText = reviewText.trim().length > 0;
        return hasText;
      });

      logger.log('[GMBDataMapper] Reviews with written text:', reviewsWithText.length);

      if (reviewsWithText.length === 0) {
        logger.log('[GMBDataMapper] No reviews with text found, returning empty array');
        return [];
      }

      // Map reviews with text to testimonials (up to 6)
      const testimonials = reviewsWithText.slice(0, 6).map((review, index) => {
        // Extract reviewer name
        const reviewerName = review.reviewer?.displayName || review.author || 'Customer';

        // Extract review text
        const reviewText = review.comment || review.text || review.reviewReply?.comment || '';

        // Extract rating (GMB uses starRating enum like FIVE, FOUR, etc.)
        let rating = 5;
        if (review.starRating) {
          const ratingMap = {
            'FIVE': 5,
            'FOUR': 4,
            'THREE': 3,
            'TWO': 2,
            'ONE': 1
          };
          rating = ratingMap[review.starRating] || 5;
        } else if (typeof review.rating === 'number') {
          rating = review.rating;
        }

        return {
          id: index + 1,
          name: reviewerName,
          role: "Customer",
          review: reviewText.trim(), // Changed from 'content' to 'review' to match TestimonialCard component
          content: reviewText.trim(), // Keep content for backward compatibility
          rating: rating,
          isRealReview: true // Flag to indicate this is a real GMB review
        };
      });

      logger.log('[GMBDataMapper] Returning', testimonials.length, 'testimonials with review text');
      return testimonials;
    }

    // NO MOCK DATA - Return empty array if no real GMB reviews
    logger.log('[GMBDataMapper] No real GMB reviews available, returning empty testimonials array');
    return [];
  }

  /**
   * Generate dynamic updates/news based on business category
   * Uses GMB photos to create visual updates
   */
  generateUpdates(gmbData = null) {
    // Check if we have GMB photos to use for updates
    if (gmbData && gmbData.photos && gmbData.photos.length > 0) {
      const photos = gmbData.photos;
      const businessName = gmbData.location?.title || 'Our Business';

      logger.log('[GMBDataMapper] Creating updates from GMB photos:', photos.length);

      // Create updates from photos
      return photos.slice(0, 8).map((photo, index) => {
        const photoUrl = photo.googleUrl || photo.url || photo.mediaFormat?.sourceUrl || null;

        return {
          id: index + 1,
          image: photoUrl,
          date: photo.createTime ? new Date(photo.createTime).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          }) : 'Recent',
          title: photo.description || photo.name || `${businessName} Update`,
          description: photo.description || `Check out our latest photos from ${businessName}`,
          isRealPhoto: true
        };
      }).filter(update => update.image); // Only include updates with valid image URLs
    }

    // Return empty array if no photos available
    logger.log('[GMBDataMapper] No GMB photos available for updates, returning empty array');
    return [];
  }

  /**
   * Generate dynamic technologies/tools based on business category
   */
  generateTechnologies(gmbData = null) {
    if (!gmbData || !gmbData.location?.title) return [];

    const detectedCategory = this.detectBusinessCategory(gmbData);

    const technologyTemplates = {
      medical: [
        { name: "Electronic Health Records", description: "Advanced EHR systems for patient care" },
        { name: "Digital Imaging", description: "State-of-the-art medical imaging technology" },
        { name: "Telemedicine", description: "Remote healthcare consultation services" }
      ],
      real_estate: [
        { name: "MLS Database", description: "Comprehensive property listing database" },
        { name: "Virtual Tours", description: "3D virtual property viewing technology" },
        { name: "Market Analytics", description: "Advanced real estate market analysis tools" }
      ],
      automotive: [
        { name: "Diagnostic Scanners", description: "Advanced automotive diagnostic equipment" },
        { name: "Digital Inspection", description: "Comprehensive digital vehicle inspection" },
        { name: "Parts Database", description: "Extensive automotive parts inventory system" }
      ],
      restaurant: [
        { name: "POS System", description: "Modern point-of-sale ordering system" },
        { name: "Kitchen Display", description: "Digital kitchen management system" },
        { name: "Online Ordering", description: "Convenient online food ordering platform" }
      ],
      legal: [
        { name: "Case Management", description: "Digital legal case management system" },
        { name: "Document Security", description: "Secure legal document management" },
        { name: "Client Portal", description: "Secure client communication platform" }
      ],
      beauty: [
        { name: "Booking System", description: "Online appointment scheduling system" },
        { name: "Treatment Tracking", description: "Digital beauty treatment records" },
        { name: "Product Database", description: "Comprehensive beauty product catalog" }
      ],
      retail: [
        { name: "Inventory Management", description: "Real-time inventory tracking system" },
        { name: "E-commerce Platform", description: "Online shopping and ordering system" },
        { name: "Customer Database", description: "Customer relationship management system" }
      ],
      general: [
        { name: "Customer Management", description: "Professional customer service system" },
        { name: "Digital Communication", description: "Modern communication tools" },
        { name: "Quality Assurance", description: "Service quality monitoring system" }
      ]
    };

    const selectedTechnologies = technologyTemplates[detectedCategory] || technologyTemplates.general;
    return selectedTechnologies.map((tech, index) => ({
      id: index + 1,
      ...tech
    }));
  }

  /**
   * Generate photos array from GMB photos data
   * USES ONLY REAL GMB PHOTOS - no mock fallback
   */
  generatePhotos(gmbPhotos = null, templateType = 'medical') {
    // Only return GMB photos if available, no fallback to mock photos
    if (gmbPhotos && gmbPhotos.length > 0) {
      logger.log('[GMBDataMapper] Using real GMB photos:', gmbPhotos.length);

      return gmbPhotos.slice(0, 12).map((photo, index) => {
        // Handle different photo URL formats from GMB API
        const photoUrl = photo.googleUrl || photo.url || photo.mediaFormat?.sourceUrl || null;

        return {
          id: index + 1,
          url: photoUrl,
          name: photo.name || `Business Photo ${index + 1}`,
          description: photo.description || '',
          category: photo.category || 'Business Photo',
          isRealPhoto: true // Flag to indicate this is a real GMB photo
        };
      }).filter(photo => photo.url); // Filter out any photos without URLs
    }

    // Return empty array if no GMB photos available
    logger.log('[GMBDataMapper] No GMB photos available, returning empty array');
    return [];
  }

  /**
   * Extract GMB categories as array of display names for tag generation
   */
  extractGMBCategories(categories) {
    if (!categories) return [];
    
    const result = [];
    
    // Add primary category
    if (categories.primaryCategory?.displayName) {
      result.push(categories.primaryCategory.displayName);
    }
    
    // Add additional categories
    if (categories.additionalCategories && Array.isArray(categories.additionalCategories)) {
      categories.additionalCategories.forEach(cat => {
        if (cat.displayName) {
          result.push(cat.displayName);
        }
      });
    }
    
    logger.log('[GMBDataMapper] Extracted GMB categories for tags:', result);
    return result;
  }

  /**
   * Detect business category from GMB data
   */
  detectBusinessCategory(gmbData) {
    const location = gmbData.location || {};
    const categories = location.categories || [];

    // Check primary category first
    const primaryCategory = categories.primaryCategory?.displayName?.toLowerCase() || '';

    // Check additional categories
    const additionalCategories = categories.additionalCategories || [];
    const allCategories = [primaryCategory, ...additionalCategories.map(cat => cat.displayName?.toLowerCase() || '')];

    // Business type detection based on categories
    for (const category of allCategories) {
      // Medical/Healthcare
      if (category.includes('hospital') || category.includes('medical') || category.includes('doctor') ||
        category.includes('clinic') || category.includes('health') || category.includes('physician') ||
        category.includes('dentist') || category.includes('dental') || category.includes('pharmacy')) {
        return 'medical';
      }

      // Real Estate
      if (category.includes('real estate') || category.includes('realtor') || category.includes('property') ||
        category.includes('housing') || category.includes('home') || category.includes('apartment') ||
        category.includes('commercial property') || category.includes('residential')) {
        return 'real_estate';
      }

      // Automotive
      if (category.includes('automotive') || category.includes('car repair') || category.includes('auto') ||
        category.includes('mechanic') || category.includes('garage') || category.includes('vehicle')) {
        return 'automotive';
      }

      // Restaurant/Food
      if (category.includes('restaurant') || category.includes('food') || category.includes('cafe') ||
        category.includes('bar') || category.includes('dining') || category.includes('catering')) {
        return 'restaurant';
      }

      // Legal
      if (category.includes('lawyer') || category.includes('attorney') || category.includes('legal') ||
        category.includes('law firm') || category.includes('legal services')) {
        return 'legal';
      }

      // Beauty/Salon
      if (category.includes('salon') || category.includes('beauty') || category.includes('spa') ||
        category.includes('hair') || category.includes('nail') || category.includes('massage')) {
        return 'beauty';
      }

      // Retail
      if (category.includes('store') || category.includes('shop') || category.includes('retail') ||
        category.includes('clothing') || category.includes('boutique')) {
        return 'retail';
      }
    }

    return 'general';
  }

  /**
   * Extract overall rating and review count from GMB data
   */
  extractRatingInfo(gmbData) {
    if (!gmbData || !gmbData.reviews) {
      return { rating: null, reviewCount: 0 };
    }

    const reviews = gmbData.reviews.reviews || [];
    const totalReviewCount = gmbData.reviews.totalReviewCount || reviews.length;
    const averageRating = gmbData.reviews.averageRating || null;

    // If averageRating is provided by API, use it
    if (averageRating) {
      return {
        rating: Number(averageRating).toFixed(1),
        reviewCount: totalReviewCount
      };
    }

    // Otherwise calculate from reviews array
    if (reviews.length > 0) {
      const ratingMap = {
        'FIVE': 5,
        'FOUR': 4,
        'THREE': 3,
        'TWO': 2,
        'ONE': 1
      };

      const totalRating = reviews.reduce((sum, review) => {
        let rating = 5;
        if (review.starRating) {
          rating = ratingMap[review.starRating] || 5;
        } else if (typeof review.rating === 'number') {
          rating = review.rating;
        }
        return sum + rating;
      }, 0);

      return {
        rating: (totalRating / reviews.length).toFixed(1),
        reviewCount: totalReviewCount
      };
    }

    return { rating: null, reviewCount: 0 };
  }

  /**
   * Generate FAQ based on detected business category from GMB data
   * NO MOCK DATA - Returns empty array, should use real GMB Q&A if available
   */
  generateFAQ(businessType, gmbData = null) {
    // TODO: Implement fetching real GMB Q&A when available
    // For now, return empty array - NO MOCK DATA
    logger.log('[GMBDataMapper] No real GMB FAQ available, returning empty array');
    return [];
  }

  /**
   * Generate GMB Feed data from GMB posts
   * Maps GMB local posts to gmbFeed format for templates
   */
  generateGMBFeed(gmbData = null) {
    const businessName = gmbData?.location?.title || 'Our Business';
    
    // Check if we have GMB posts
    if (!gmbData || !gmbData.posts || gmbData.posts.length === 0) {
      logger.log('[GMBDataMapper] No GMB posts available for feed');
      return {
        title: "Latest Updates",
        subtitle: `Stay updated with news from ${businessName}`,
        showSection: false,
        posts: []
      };
    }

    logger.log('[GMBDataMapper] Processing GMB posts for feed:', gmbData.posts.length);

    // Map GMB posts to feed format
    const posts = gmbData.posts.slice(0, 6).map((post, index) => {
      // Extract post ID
      const postId = post.name || `post-${index}`;
      
      // Extract media (photos)
      const media = post.media || [];
      
      // Extract call to action
      const callToAction = post.callToAction || null;

      return {
        id: postId,
        summary: post.summary || '',
        topicType: post.topicType || 'STANDARD',
        createTime: post.createTime || null,
        updateTime: post.updateTime || null,
        media: media.map(m => ({
          googleUrl: m.googleUrl || m.sourceUrl || null,
          sourceUrl: m.sourceUrl || null,
          mediaFormat: m.mediaFormat || 'PHOTO'
        })),
        callToAction: callToAction ? {
          actionType: callToAction.actionType || 'LEARN_MORE',
          url: callToAction.url || null
        } : null,
        event: post.event || null,
        offer: post.offer || null,
        state: post.state || 'LIVE'
      };
    });

    const validPosts = posts.filter(p => p.summary && p.summary.trim().length > 0);
    logger.log('[GMBDataMapper] Valid posts with content:', validPosts.length);

    return {
      title: "Latest Updates",
      subtitle: `Stay updated with news from ${businessName}`,
      showSection: validPosts.length > 0,
      posts: validPosts
    };
  }

  /**
   * Generate technologies for Template 2
   */
  generateTechnologies(businessType) {
    return [
      {
        id: 1,
        title: "Latest Technology",
        description: "We use the latest technology and equipment in our services.",
        image: `/images/${businessType}-tech-1.jpg`,
        date: new Date().toLocaleDateString(),
        comments: 0
      }
    ];
  }

  // Template 3 mapper - inherits from Template 1 with location data and keywords
  mapToTemplate3(gmbData) {
    const baseData = this.mapToTemplate1(gmbData);
    // Include keywordsData for Tags section
    return {
      ...baseData,
      keywordsData: gmbData.keywordsData || []
    };
  }

  // Template 4 mapper - inherits from Template 1 with location data
  mapToTemplate4(gmbData) {
    const baseData = this.mapToTemplate1(gmbData);
    return baseData;
  }

  // Template 5 mapper - inherits from Template 1 with location data
  mapToTemplate5(gmbData) {
    const baseData = this.mapToTemplate1(gmbData);
    return baseData;
  }

  // Template 6 mapper - inherits from Template 1 with location data
  mapToTemplate6(gmbData) {
    const baseData = this.mapToTemplate1(gmbData);
    return baseData;
  }

  // Template 7 mapper - inherits from Template 1 with location data
  mapToTemplate7(gmbData) {
    const baseData = this.mapToTemplate1(gmbData);
    return baseData;
  }
}

export default new GMBDataMapper();
